import {
  displayDate,
  displayReportMessageType,
} from '~/plugins/functions/display';

// For DTO ReportTokenUsage
export interface IModelGETReportTokenUsageRequest {
  userId: number;
  page: number;
  limit: number;
  count: boolean;
}

export interface IModelGETReportTokenUsageResponse {
  data: IModelGETReportTokenUsageResponseData[];
  totalItems: number;
}

export interface IModelGETReportTokenUsageResponseData {
  avatar: string;
  conversationId: string;
  conversationMessageId: string;
  conversationName: string;
  createdAt: string | Date;
  createdBy: string;
  id: string;
  inputToken: number;
  modelName: string;
  outputToken: number;
  presetId: string;
  presetName: string;
  type: string;
  updatedBy: string | null;
  userId: number;
  username: string;
  _id: string;
}

export class ModelGETReportTokenUsage {
  dataList: ModelGETReportTokenUsageData[];
  totalItems: number;

  constructor(props = {} as IModelGETReportTokenUsageResponse) {
    props = props ?? ({} as IModelGETReportTokenUsageResponse);
    this.dataList = (props.data || []).map(
      (e: IModelGETReportTokenUsageResponseData) =>
        new ModelGETReportTokenUsageData(e)
    );
    this.totalItems = props.totalItems || 0;
  }
}

export class ModelGETReportTokenUsageData
  implements IModelGETReportTokenUsageResponseData
{
  avatar: string;
  conversationId: string;
  conversationMessageId: string;
  conversationName: string;
  createdAt: string | Date;
  createdBy: string;
  id: string;
  inputToken: number;
  modelName: string;
  outputToken: number;
  presetId: string;
  presetName: string;
  type: string;
  updatedBy: string | null;
  userId: number;
  username: string;
  _id: string;

  displayCreatedAt: string;
  displayType: string;
  displayInputToken: string;
  displayOutputToken: string;

  constructor(props = {} as IModelGETReportTokenUsageResponseData) {
    props = props ?? ({} as IModelGETReportTokenUsageResponseData);
    this.avatar = props.avatar || '';
    this.conversationId = props.conversationId || '';
    this.conversationMessageId = props.conversationMessageId || '';
    this.conversationName = props.conversationName || '';
    this.createdAt = props.createdAt || '';
    this.createdBy = props.createdBy || '';
    this.id = props.id || '';
    this.inputToken = props.inputToken || 0;
    this.modelName = props.modelName || '';
    this.outputToken = props.outputToken || 0;
    this.presetId = props.presetId || '';
    this.presetName = props.presetName || '';
    this.type = props.type || '';
    this.updatedBy = props.updatedBy || '';
    this.userId = props.userId || 0;
    this.username = props.username || '';
    this._id = props._id || '';

    this.displayCreatedAt = displayDate(this.createdAt);
    this.displayType = displayReportMessageType(this.type);
    this.displayInputToken = this.inputToken.toLocaleString();
    this.displayOutputToken = this.outputToken.toLocaleString();
  }
}

export interface IModelGETReportTokenUsageSummaryRequest {
  userId: number;
  timezone: string;
  startDate: string;
  endDate: string;
}

export interface IModelGETReportTokenUsageSummaryResponse {
  query: IModelGETReportTokenUsageSummaryResponseQuery;
  summary: IModelGETReportTokenUsageSummaryResponseSummary;
  daily: IModelGETReportTokenUsageSummaryResponseDaily[];
}

export interface IModelGETReportTokenUsageSummaryResponseQuery {
  timezone: string;
  userId: number;
  startDate: string;
  endDate: string;
}

export interface IModelGETReportTokenUsageSummaryResponseSummary {
  inputToken: number;
  outputToken: number;
  totalToken: number;
  currentCost: number;
  currentCostThb: number;
  conversationCount: number;
  user: {
    id: number;
    username: string;
    avatar: string;
  };
  feedback: {
    total: { thumbsUp: number; thumbsDown: number };
    gpt_oss: { thumbsUp: number; thumbsDown: number };
    gemini: { thumbsUp: number; thumbsDown: number };
    claude: { thumbsUp: number; thumbsDown: number };
  };
  avgLatency: {
    gpt_oss: number;
    gemini: number;
    claude: number;
  };
}

export interface IModelGETReportTokenUsageSummaryResponseDaily {
  date: string;
  inputToken: number;
  outputToken: number;
  totalToken: number;
}

export class ModelGETReportTokenUsageSummary {
  query: ModelGETReportTokenUsageSummaryQuery;
  summary: ModelGETReportTokenUsageSummarySummary;
  dailyList: ModelGETReportTokenUsageSummaryDaily[];

  constructor(props = {} as IModelGETReportTokenUsageSummaryResponse) {
    props = props ?? ({} as IModelGETReportTokenUsageSummaryResponse);
    this.query = new ModelGETReportTokenUsageSummaryQuery(props.query);
    this.summary = new ModelGETReportTokenUsageSummarySummary(props.summary);
    this.dailyList = (props.daily || []).map(
      (e: IModelGETReportTokenUsageSummaryResponseDaily) =>
        new ModelGETReportTokenUsageSummaryDaily(e)
    );
  }
}

export class ModelGETReportTokenUsageSummaryQuery
  implements IModelGETReportTokenUsageSummaryResponseQuery
{
  timezone: string;
  userId: number;
  startDate: string;
  endDate: string;

  constructor(props = {} as IModelGETReportTokenUsageSummaryResponseQuery) {
    props = props ?? ({} as IModelGETReportTokenUsageSummaryResponseQuery);
    this.timezone = props.timezone || '';
    this.userId = props.userId || 0;
    this.startDate = props.startDate || '';
    this.endDate = props.endDate || '';
  }
}

export class ModelGETReportTokenUsageSummarySummary
  implements IModelGETReportTokenUsageSummaryResponseSummary
{
  inputToken: number;
  outputToken: number;
  totalToken: number;
  currentCost: number;
  currentCostThb: number;
  conversationCount: number;
  user: {
    id: number;
    username: string;
    avatar: string;
  };
  feedback: {
    total: { thumbsUp: number; thumbsDown: number };
    gpt_oss: { thumbsUp: number; thumbsDown: number };
    gemini: { thumbsUp: number; thumbsDown: number };
    claude: { thumbsUp: number; thumbsDown: number };
  };
  avgLatency: {
    gpt_oss: number;
    gemini: number;
    claude: number;
  };

  constructor(props = {} as IModelGETReportTokenUsageSummaryResponseSummary) {
    props = props ?? ({} as IModelGETReportTokenUsageSummaryResponseSummary);
    this.inputToken = props.inputToken || 0;
    this.outputToken = props.outputToken || 0;
    this.totalToken = props.totalToken || 0;
    this.currentCost = props.currentCost || 0;
    this.currentCostThb = props.currentCostThb || 0;
    this.conversationCount = props.conversationCount || 0;
    this.user = props.user || {};
    this.feedback = props.feedback || {};
    this.avgLatency = props.avgLatency || {};
  }
}

export class ModelGETReportTokenUsageSummaryDaily
  implements IModelGETReportTokenUsageSummaryResponseDaily
{
  date: string;
  inputToken: number;
  outputToken: number;
  totalToken: number;

  constructor(props = {} as IModelGETReportTokenUsageSummaryResponseDaily) {
    props = props ?? ({} as IModelGETReportTokenUsageSummaryResponseDaily);
    this.date = props.date || '';
    this.inputToken = props.inputToken || 0;
    this.outputToken = props.outputToken || 0;
    this.totalToken = props.totalToken || 0;
  }
}

// For DTO ReportTokenUsageId
export interface IModelGETReportTokenUsageIdRequest {
  id: string;
}

export interface IModelGETReportTokenUsageIdRawRequestResponse {
  type: string;
  content: string;
}

export interface IModelGETReportTokenUsageIdRawResponseResponse {
  type: string;
  content: string;
}

export interface IModelGETReportTokenUsageIdResponse {
  _id: string;
  presetId: string;
  presetName: string;
  modelName: string;
  type: string;
  conversationId: string;
  conversationMessageId: string;
  rawRequest: IModelGETReportTokenUsageIdRawRequestResponse[];
  rawResponse: IModelGETReportTokenUsageIdRawResponseResponse[];
  inputToken: number;
  outputToken: number;
  createdAt: string | Date;
  conversationName: string;
  userId: number;
  createdBy: string;
  id: string;
  username: string;
  avatar: string;
  updatedBy: any;
}

export class ModelGETReportTokenUsageIdRawRequest {
  type: string;
  content: string;

  constructor(props = {} as IModelGETReportTokenUsageIdRawRequestResponse) {
    props = props ?? ({} as IModelGETReportTokenUsageIdRawRequestResponse);
    this.type = props.type || '';
    this.content = props.content || '';
  }
}

export class ModelGETReportTokenUsageIdRawResponse {
  type: string;
  content: string;

  constructor(props = {} as IModelGETReportTokenUsageIdRawResponseResponse) {
    props = props ?? ({} as IModelGETReportTokenUsageIdRawResponseResponse);
    this.type = props.type || '';
    this.content = props.content || '';
  }
}

export class ModelGETReportTokenUsageId {
  _id: string;
  presetId: string;
  presetName: string;
  modelName: string;
  type: string;
  conversationId: string;
  conversationMessageId: string;
  rawRequestList: ModelGETReportTokenUsageIdRawRequest[];
  rawResponseList: ModelGETReportTokenUsageIdRawResponse[];
  inputToken: number;
  outputToken: number;
  createdAt: string | Date;
  conversationName: string;
  userId: number;
  createdBy: string;
  id: string;
  username: string;
  avatar: string;
  updatedBy: any;

  displayCreatedAt: string;
  displayType: string;
  displayInputToken: string;
  displayOutputToken: string;

  constructor(props = {} as IModelGETReportTokenUsageIdResponse) {
    props = props ?? ({} as IModelGETReportTokenUsageIdResponse);
    this._id = props._id || '';
    this.presetId = props.presetId || '';
    this.presetName = props.presetName || '';
    this.modelName = props.modelName || '';
    this.type = props.type || '';
    this.conversationId = props.conversationId || '';
    this.conversationMessageId = props.conversationMessageId || '';
    this.rawRequestList = (props.rawRequest || []).map(
      (e: IModelGETReportTokenUsageIdRawRequestResponse) =>
        new ModelGETReportTokenUsageIdRawRequest(e)
    );
    this.rawResponseList = (props.rawResponse || []).map(
      (e: IModelGETReportTokenUsageIdRawResponseResponse) =>
        new ModelGETReportTokenUsageIdRawResponse(e)
    );
    this.inputToken = props.inputToken || 0;
    this.outputToken = props.outputToken || 0;
    this.createdAt = props.createdAt || '';
    this.conversationName = props.conversationName || '';
    this.userId = props.userId || 0;
    this.createdBy = props.createdBy || '';
    this.id = props.id || '';
    this.username = props.username || '';
    this.avatar = props.avatar || '';
    this.updatedBy = props.updatedBy || null;

    this.displayCreatedAt = displayDate(this.createdAt);
    this.displayType = displayReportMessageType(this.type);
    this.displayInputToken = this.inputToken.toLocaleString();
    this.displayOutputToken = this.outputToken.toLocaleString();
  }
}
